import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Trash2, ChevronUp, ChevronDown, ImageIcon, Video, Eye, EyeOff, RefreshCcw, Play,
} from "lucide-react";
import {
  listHeroMedia,
  createHeroMedia,
  updateHeroMedia,
  deleteHeroMedia,
  uploadMedia,
  absoluteUrl,
} from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const IMAGE_INTERVAL_MS = 6500;

const PAGES = [
  { key: "home", label: "Home · Hero" },
  { key: "home-parallax", label: "Home · Parallax" },
  { key: "menus", label: "Menus" },
  { key: "location", label: "Location" },
  { key: "reservation", label: "Reservation" },
  { key: "contact", label: "Contact" },
];

export default function AdminHero() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === "admin";
  const [allMedia, setAllMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activePage, setActivePage] = useState("home");
  const [delDlg, setDelDlg] = useState({ open: false, id: null, label: "" });
  const [previewActive, setPreviewActive] = useState(0);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listHeroMedia(false);
      setAllMedia(list);
      setPreviewActive(0);
    } catch {
      toast.error("Failed to load hero media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Media for currently selected page
  const media = useMemo(
    () => allMedia.filter((m) => (m.page || "home") === activePage),
    [allMedia, activePage]
  );

  // Counts per page for tab badges
  const countsByPage = useMemo(() => {
    const c = {};
    PAGES.forEach((p) => (c[p.key] = 0));
    allMedia.forEach((m) => {
      const k = m.page || "home";
      if (c[k] !== undefined) c[k] += 1;
    });
    return c;
  }, [allMedia]);

  // Preview slideshow (active items only, images auto-advance)
  const activeItems = media.filter((m) => m.is_active);
  useEffect(() => {
    setPreviewActive(0);
  }, [activePage]);
  useEffect(() => {
    if (activeItems.length <= 1) return;
    const cur = activeItems[previewActive % activeItems.length];
    if (cur?.type === "image") {
      timerRef.current = setTimeout(
        () => setPreviewActive((i) => (i + 1) % activeItems.length),
        IMAGE_INTERVAL_MS,
      );
      return () => clearTimeout(timerRef.current);
    }
  }, [previewActive, activeItems]);

  async function handleFile(file) {
    if (!file) return;
    const isVideo = /^video\//.test(file.type);
    const isImage = /^image\//.test(file.type);
    if (!isImage && !isVideo) {
      toast.error("Please choose an image (PNG/JPG/WEBP/GIF) or video (MP4/WEBM/MOV).");
      return;
    }
    const maxBytes = isVideo ? 40 * 1024 * 1024 : 6 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`Max ${isVideo ? "video" : "image"} size is ${maxBytes / (1024 * 1024)} MB.`);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await uploadMedia(file, (evt) => {
        if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
      });
      // Add as new hero media at the end of the currently-selected page
      const pageMedia = allMedia.filter((m) => (m.page || "home") === activePage);
      const nextOrder = pageMedia.length > 0 ? Math.max(...pageMedia.map((m) => m.order)) + 1 : 0;
      await createHeroMedia({
        page: activePage,
        type: res.media_type,
        url: res.url,
        order: nextOrder,
        is_active: true,
      });
      toast.success(`${res.media_type === "video" ? "Video" : "Image"} added to ${activePage}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function toggleActive(m) {
    try {
      await updateHeroMedia(m.id, { is_active: !m.is_active });
      await load();
    } catch {
      toast.error("Failed to update");
    }
  }

  async function moveOrder(m, direction) {
    const sorted = [...media].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === m.id);
    const swapWith = direction === "up" ? sorted[idx - 1] : sorted[idx + 1];
    if (!swapWith) return;
    try {
      await Promise.all([
        updateHeroMedia(m.id, { order: swapWith.order }),
        updateHeroMedia(swapWith.id, { order: m.order }),
      ]);
      await load();
    } catch {
      toast.error("Failed to reorder");
    }
  }

  async function confirmDelete() {
    try {
      await deleteHeroMedia(delDlg.id);
      toast.success("Removed");
      setDelDlg({ open: false, id: null, label: "" });
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  }

  return (
    <div data-testid="page-admin-hero">
      <main className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14">
        <div className="flex items-end justify-between gap-8 mb-10">
          <div>
            <div className="overline">Hero</div>
            <h1 className="mt-4 serif text-4xl md:text-5xl font-light leading-tight text-foreground">
              The first thing they see.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
              Manage the homepage hero. Add multiple images to create a slideshow, or add a video
              for a moving cover. Reorder to control the sequence.
            </p>
          </div>
          <button
            data-testid="admin-hero-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-[0.72rem] uppercase tracking-[0.22em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
          >
            <RefreshCcw size={12} strokeWidth={1.5} />
            Refresh
          </button>
        </div>

        {/* Page tabs */}
        <div className="mb-8 flex flex-wrap items-center gap-2 border-y border-border py-4">
          <span className="text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground mr-2">Page</span>
          {PAGES.map((p) => {
            const active = activePage === p.key;
            return (
              <button
                key={p.key}
                data-testid={`admin-hero-page-${p.key}`}
                onClick={() => setActivePage(p.key)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[0.68rem] uppercase tracking-[0.2em] border transition-colors ${
                  active
                    ? "border-primary text-primary bg-primary/10"
                    : "border-border text-foreground/70 hover:text-foreground hover:border-foreground/50"
                }`}
              >
                {p.label}
                <span className="text-muted-foreground tabular-nums">{countsByPage[p.key]}</span>
              </button>
            );
          })}
        </div>

        {/* Upload dropzone */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
          className="hidden"
          data-testid="admin-hero-file-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          data-testid="admin-hero-upload"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full mb-10 flex items-center justify-center gap-3 border border-dashed border-border py-8 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Upload size={14} strokeWidth={1.5} className="animate-pulse" />
              Uploading {uploadProgress}%
            </>
          ) : (
            <>
              <Upload size={14} strokeWidth={1.5} />
              Add image (PNG · JPG · WEBP · Max 6MB) or video (MP4 · WEBM · Max 40MB)
            </>
          )}
        </button>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* List */}
          <div className="lg:col-span-7 space-y-4">
            {loading ? (
              <div data-testid="admin-hero-loading" className="py-24 text-center text-muted-foreground italic serif text-xl">
                <span className="ornament">Loading media</span>
              </div>
            ) : media.length === 0 ? (
              <div className="border border-dashed border-border p-16 text-center">
                <ImageIcon size={28} className="mx-auto text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">
                  No hero media yet. Upload one to fill the homepage hero.
                </p>
              </div>
            ) : (
              media
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((m, idx, arr) => {
                  const Icon = m.type === "video" ? Video : ImageIcon;
                  return (
                    <div
                      key={m.id}
                      data-testid={`admin-hero-row-${m.id}`}
                      className={`border p-4 flex items-center gap-4 bg-card transition-opacity ${
                        m.is_active ? "border-border" : "border-border opacity-60"
                      }`}
                    >
                      {/* Thumbnail */}
                      <div className="w-24 h-16 shrink-0 border border-border bg-black overflow-hidden flex items-center justify-center relative">
                        {m.type === "video" ? (
                          <>
                            <video
                              src={absoluteUrl(m.url)}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Play size={14} strokeWidth={1.5} className="text-white/90" />
                            </div>
                          </>
                        ) : (
                          <img
                            src={absoluteUrl(m.url)}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      {/* Meta */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
                          <Icon size={11} strokeWidth={1.5} className="text-primary" />
                          {m.type} · order {m.order}
                        </div>
                        <div className="mt-1 text-sm text-foreground/80 truncate">
                          {m.url.startsWith("/api/files/")
                            ? m.url.split("/").pop()
                            : m.url}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground">
                            Device
                          </span>
                          <select
                            data-testid={`admin-hero-device-${m.id}`}
                            value={m.device || "all"}
                            onChange={async (e) => {
                              const v = e.target.value;
                              try {
                                const updated = await updateHeroMedia(m.id, { device: v });
                                setAllMedia((prev) =>
                                  prev.map((x) => (x.id === m.id ? { ...x, ...updated } : x)),
                                );
                              } catch {
                                toast.error("Failed to update device");
                              }
                            }}
                            className="bg-background border border-border text-foreground text-[0.65rem] uppercase tracking-[0.2em] px-2 py-1 focus:border-primary focus:outline-none"
                          >
                            <option value="all">All</option>
                            <option value="desktop">Desktop only</option>
                            <option value="mobile">Mobile only</option>
                          </select>
                        </div>
                      </div>

                      {/* Controls */}
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          data-testid={`admin-hero-up-${m.id}`}
                          disabled={idx === 0}
                          onClick={() => moveOrder(m, "up")}
                          className="p-2 text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move up"
                        >
                          <ChevronUp size={14} strokeWidth={1.5} />
                        </button>
                        <button
                          data-testid={`admin-hero-down-${m.id}`}
                          disabled={idx === arr.length - 1}
                          onClick={() => moveOrder(m, "down")}
                          className="p-2 text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move down"
                        >
                          <ChevronDown size={14} strokeWidth={1.5} />
                        </button>
                        <div className="flex items-center gap-2 px-2" title={m.is_active ? "Visible on site" : "Hidden"}>
                          {m.is_active ? (
                            <Eye size={13} strokeWidth={1.5} className="text-primary" />
                          ) : (
                            <EyeOff size={13} strokeWidth={1.5} className="text-muted-foreground" />
                          )}
                          <Switch
                            data-testid={`admin-hero-active-${m.id}`}
                            checked={m.is_active}
                            onCheckedChange={() => toggleActive(m)}
                          />
                        </div>
                        {isAdmin && (
                          <button
                            data-testid={`admin-hero-delete-${m.id}`}
                            onClick={() =>
                              setDelDlg({
                                open: true,
                                id: m.id,
                                label: m.type === "video" ? "video" : "image",
                              })
                            }
                            className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={13} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
            )}
          </div>

          {/* Preview */}
          <aside className="lg:col-span-5">
            <div className="sticky top-40">
              <div className="overline mb-3">Live Preview</div>
              <div className="relative aspect-[3/4] md:aspect-square w-full overflow-hidden border border-border bg-black">
                {activeItems.length === 0 ? (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                    Nothing active to show.
                  </div>
                ) : (
                  activeItems
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((m, i) => {
                      const isActive = i === previewActive % activeItems.length;
                      const src = absoluteUrl(m.url);
                      return (
                        <div
                          key={m.id}
                          className={`absolute inset-0 transition-opacity duration-[1000ms] ${
                            isActive ? "opacity-100" : "opacity-0"
                          }`}
                        >
                          {m.type === "video" ? (
                            <video
                              key={src + isActive}
                              src={src}
                              className="w-full h-full object-cover"
                              autoPlay={isActive}
                              muted
                              playsInline
                              loop={activeItems.length === 1}
                              onEnded={() =>
                                activeItems.length > 1 &&
                                setPreviewActive((idx) => (idx + 1) % activeItems.length)
                              }
                            />
                          ) : (
                            <img src={src} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                      );
                    })
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/10 to-background/70 pointer-events-none" />
                <div className="absolute bottom-4 left-4 text-primary text-xs uppercase tracking-[0.22em]">
                  ✦ NELUM
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                {activeItems.length > 1
                  ? `${activeItems.length} slides — auto-rotate every ${IMAGE_INTERVAL_MS / 1000}s`
                  : activeItems.length === 1
                  ? "Single item — will render statically."
                  : "No active items."}
              </p>
            </div>
          </aside>
        </div>
      </main>

      <AlertDialog
        open={delDlg.open}
        onOpenChange={(v) => setDelDlg((s) => ({ ...s, open: v }))}
      >
        <AlertDialogContent
          data-testid="hero-delete-dialog"
          className="bg-card border border-border rounded-none text-foreground"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="serif text-2xl font-light">
              Delete this {delDlg.label}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-sm">
              Permanently remove from the hero. If uploaded, the file is soft-deleted from storage. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-border bg-transparent hover:bg-accent text-foreground/80">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="hero-delete-confirm"
              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
